import { Box, Button, InputLabel, Stack, Typography } from "@mui/material";
import { AssignScheduleProps, ShiftTyeModalProps } from "../../interface/modal";
import ModalContainer from "../UIElements/modal-container";
import { useTheme } from "@mui/material/styles";
import { InputCustomSize, TextArea } from "../UIElements/input-ui";
import { labelSty } from "../UIElements/styles-input";

import { useState } from "react";
import useResponsive from "../../hooks/useResponsive";
import { useMutation } from "@tanstack/react-query";
import {
  ScheduleCreateProps,
  ShiftCreateProps,
} from "../../interface/schedule";
import { useAuth } from "../../hooks/useAuth";
import { apiCreateSchedule, apiCreateShift } from "../../api/schedules";
import { useNotificationService } from "../notification";
import { LoadingButton } from "@mui/lab";
import CustomDatePicker from "../UIElements/date-picker";
import dayjs, { Dayjs } from "dayjs";
import CustomTimePicker from "../UIElements/time-picker";
import SelectInput from "../UIElements/select";
import {
  isDateWithinRange,
  updateDatePreservingTime,
} from "../../utils/url-id";

export const ShiftTypeModal = ({
  open,
  start,
  end,
  handleModal,
  employeeList,
  closeModal,
  scheduleId,
  loading,
  setRefresh,
}: ShiftTyeModalProps) => {
  const theme = useTheme();
  const isSmall = useResponsive("down", "sm", "xl");
  const { showNotification } = useNotificationService();
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [startTime, setStartTime] = useState<Dayjs | null>(selectedDate);
  const [endTime, setEndTime] = useState<Dayjs | null>(selectedDate);

  const [employee_name, setEmployee_name] = useState("");

  const handleSelectedDate = (newValue: Dayjs | null) => {
    if (newValue?.isBefore(dayjs(), "day")) {
      showNotification("Shift date cannot be before today", "error");
      return;
    }

    if (!newValue) return;
    setSelectedDate(newValue);
  };
  const handleStartTime = (newValue: Dayjs | null) => {
    if (!newValue) return;
    setStartTime(newValue);
  };
  const handleEndTime = (newValue: Dayjs | null) => {
    if (!newValue) return;
    setEndTime(newValue);
  };

  const mutation = useMutation({
    mutationFn: (fieldValues: ShiftCreateProps) => apiCreateShift(fieldValues),
    onSuccess: (data) => {
      // Handle error
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
      }
      //  Handle success
      if ("data" in data) {
        showNotification(data?.data?.message ?? "", "success");
        setRefresh((prev) => !prev);
        handleModal();
        closeModal();
      }
    },
    onError: (error) => {
      console.error("Schedule failed", error);
      // Handle error
      showNotification("Schedule failed", "error");
    },
  });

  const handleSubmit = (e: any) => {
    e.preventDefault();
    // check if date is within range
    const rangeValid = isDateWithinRange(selectedDate, start, end);
    if (!rangeValid) {
      showNotification("Date is not within the schedule range/date", "warning");
      return;
    }

    // get the employeeId
    const employeeId = employeeList?.find(
      (data) => data.medic_info.name === employee_name,
    );

    if (selectedDate.isBefore(dayjs(), "day")) {
      showNotification("Shift date must be a future date", "error");
      return;
    }

    if (!startTime || !endTime) {
      showNotification("Please select a valid start and end time", "error");
      return;
    }

    // convert the date to the selected  date
    const updateStartTime = updateDatePreservingTime(selectedDate, startTime);
    const updateEndTime = updateDatePreservingTime(selectedDate, endTime);

    if (!employeeId) {
      showNotification("Employee not selected", "error");
      return;
    }
    const form_data = {
      schedule_id: scheduleId,
      employee_id: employeeId?._id ?? "",
      start_time: updateStartTime?.toISOString() ?? "",
      end_time: updateEndTime?.toISOString() ?? "",
    };

    mutation.mutate(form_data);
  };

  return (
    <ModalContainer
      sx={{
        borderRadius: "16px",
      }}
      size={"sm"}
      openModal={open}
      fullWidth={true}
      handleModal={() => {}}
    >
      <Box sx={{ background: "#fff", padding: 4, borderRadius: "16px" }}>
        <Typography
          variant="h6"
          sx={{
            fontFamily: "Outfit",
            fontWeight: 500,
            color: "#292925",
          }}
        >
          Create New Shift
        </Typography>
        <Box component={"form"} onSubmit={(e: any) => handleSubmit(e)}>
          <Stack sx={{}}>
            <SelectInput
              label={"Select Employee"}
              value={employee_name}
              onChange={(e) => setEmployee_name(e.target.value)}
              options={employeeList?.map((val) => ({
                value: `${val?.medic_info?.name}`,
                label: `${val?.medic_info?.name} -  ${val?.job_info.job_title} - ${val?.job_info.department}`,
              }))}
            />
          </Stack>
          <Stack sx={{ width: "100%" }}>
            <InputLabel sx={{ ...labelSty }}>Select Shift Date:</InputLabel>
            <CustomDatePicker
              label={""}
              value={selectedDate}
              onChange={handleSelectedDate}
            />
          </Stack>

          <Stack sx={{ my: 3 }}>
            <InputLabel sx={{ ...labelSty }}>Shift time</InputLabel>
            <Stack
              direction={isSmall ? "column" : "row"}
              justifyContent={"space-between"}
            >
              <Stack sx={{ width: isSmall ? "100%" : "48%" }}>
                <InputLabel sx={{ ...labelSty }}>Start:</InputLabel>
                <CustomTimePicker
                  label={""}
                  value={startTime}
                  onChange={handleStartTime}
                />
                {/* <Datetime
                  className="time-sty"
                  value={startTime}
                  onChange={(date) => setStartTime(moment(date))}
                /> */}
              </Stack>
              <Stack sx={{ width: isSmall ? "100%" : "48%" }}>
                <InputLabel sx={{ ...labelSty }}>End:</InputLabel>
                <CustomTimePicker
                  label=""
                  value={endTime}
                  onChange={handleEndTime}
                />
                {/* <Datetime
                  className="time-sty"
                  value={endTime}
                  onChange={(date) => setEndTime(moment(date))}
                /> */}
              </Stack>
            </Stack>
          </Stack>

          <Stack direction={"row"} justifyContent={"flex-end"} sx={{ gap: 4 }}>
            <Button
              onClick={handleModal}
              sx={{
                width: 150,
                borderRadius: "12px",
                background: theme.palette.lightBlue.light,
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <LoadingButton
              loading={mutation.isPending}
              type="submit"
              sx={{ width: 150, borderRadius: "12px" }}
              variant="contained"
            >
              Create
            </LoadingButton>
          </Stack>
        </Box>
      </Box>
    </ModalContainer>
  );
};

export const AssignSchedule = ({
  open,
  modalHandler,
  setRefresh,
}: AssignScheduleProps) => {
  const theme = useTheme();
  const { showNotification } = useNotificationService();
  const [selectedDate, setSelectedDate] = useState<{
    start_date: Dayjs | null;
    end_date: Dayjs | null;
  }>({
    start_date: dayjs(),
    end_date: dayjs(),
  });
  const [schedule_data, setSchedule_data] = useState({
    schedule_name: "",
    description: "",
  });

  const mutation = useMutation({
    mutationFn: (fieldValues: ScheduleCreateProps) =>
      apiCreateSchedule(fieldValues),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
      }
      if ("data" in data) {
        modalHandler();
        setRefresh((prev) => !prev);
      }
    },
    onError: (error) => {
      console.error("Schedule failed", error);
      showNotification("Schedule failed", "error");
    },
  });

  const handleStartDateChange = (newValue: Dayjs | null) => {
    if (!newValue) return;

    if (dayjs(newValue).isBefore(dayjs(), "day")) {
      showNotification("Start date cannot be before today", "warning");
      return;
    }

    setSelectedDate((prev) => {
      let newEnd = prev.end_date;
      if (newEnd && dayjs(newEnd).isBefore(newValue, "day")) {
        newEnd = newValue;
      }
      return { start_date: newValue, end_date: newEnd };
    });
  };

  const handleEndDateChange = (newValue: Dayjs | null) => {
    if (!newValue) return;

    if (
      selectedDate.start_date &&
      dayjs(newValue).isBefore(selectedDate.start_date, "day")
    ) {
      showNotification("End date cannot be earlier than start date", "warning");
      return;
    }

    setSelectedDate((prev) => ({ ...prev, end_date: newValue }));
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();

    const { start_date, end_date } = selectedDate;

    if (!start_date || !end_date) {
      showNotification("Please select both start and end dates", "error");
      return;
    }

    if (dayjs(start_date).isBefore(dayjs(), "day")) {
      showNotification("Start date cannot be before today", "error");
      return;
    }

    if (dayjs(end_date).isBefore(start_date, "day")) {
      showNotification("End date cannot be earlier than start date", "error");
      return;
    }

    const form_data = {
      ...schedule_data,
      start_date: dayjs(start_date).toISOString(),
      end_date: dayjs(end_date).toISOString(),
    };

    mutation.mutate(form_data);
  };

  return (
    <ModalContainer
      sx={{ borderRadius: "16px" }}
      size={"xs"}
      openModal={open}
      fullWidth={true}
      handleModal={() => {}}
    >
      <Box sx={{ background: "#fff", padding: 3, borderRadius: "16px" }}>
        <Typography
          variant="h6"
          sx={{ fontFamily: "Outfit", fontWeight: 500, color: "#292925" }}
        >
          Assign Schedule
        </Typography>

        <Box component={"form"} onSubmit={(e: any) => handleSubmit(e)}>
          {/* Schedule Name */}
          <Stack sx={{ my: 3 }}>
            <InputLabel sx={{ ...labelSty }}>Schedule Name</InputLabel>
            <InputCustomSize
              placeholder="Enter text here"
              value={schedule_data?.schedule_name}
              onchange={(e: any) =>
                setSchedule_data((prev) => ({
                  ...prev,
                  schedule_name: e.target.value,
                }))
              }
              type="text"
              styles={{}}
              size="medium"
            />
          </Stack>

          {/* Description */}
          <Stack sx={{ my: 3 }}>
            <InputLabel sx={{ ...labelSty }}>Description</InputLabel>
            <TextArea
              placeholder="Enter text here."
              value={schedule_data?.description}
              onchange={(e: any) =>
                setSchedule_data((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            />
          </Stack>

          {/* Date Selection */}
          <Stack sx={{ my: 3 }}>
            <InputLabel sx={{ ...labelSty }}>Shift Date</InputLabel>
            <Stack direction={"column"} justifyContent={"space-between"}>
              <Stack sx={{ width: "100%" }}>
                <InputLabel sx={{ ...labelSty }}>Start Date:</InputLabel>
                <CustomDatePicker
                  label=""
                  value={selectedDate?.start_date}
                  onChange={handleStartDateChange}
                  minDate={dayjs()} // disable past days
                />
              </Stack>

              <Stack sx={{ width: "100%" }}>
                <InputLabel sx={{ ...labelSty }}>End Date:</InputLabel>
                <CustomDatePicker
                  label=""
                  value={selectedDate?.end_date}
                  onChange={handleEndDateChange}
                  minDate={selectedDate?.start_date || dayjs()} // disable before start
                />
              </Stack>
            </Stack>
          </Stack>

          {/* Buttons */}
          <Stack
            direction={"row"}
            justifyContent={"space-around"}
            sx={{ gap: 3 }}
          >
            <Button
              onClick={modalHandler}
              sx={{
                width: 150,
                borderRadius: "12px",
                background: theme.palette.lightBlue.light,
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <LoadingButton
              loading={mutation.isPending}
              type="submit"
              sx={{ width: 150, borderRadius: "12px" }}
              variant="contained"
            >
              Create
            </LoadingButton>
          </Stack>
        </Box>
      </Box>
    </ModalContainer>
  );
};
